import React, { useState, useCallback } from "react"
import { useTheme } from "~/ui/styled"
import Animated, {
  useSharedValue,
  withSequence,
  withTiming,
  useAnimatedStyle,
  runOnJS
} from "~/ui/reanimated"
import { ColumnFlex, CenterFlex, Text, Button, Image } from "~components/El"
import { Avatar } from "~widgets/avatar"
import { Message, useStores } from "~models"
import { useChatList } from "~hooks/useChatList"
import useGroupActions from "~hooks/useGroupActions"
import { globalToast } from "~utils";
import { PopupRoot, PopupImageBackground, PopupClose } from "../components/PopupShell"

const transferReceiveBg = require("~assets/images/chat/transfer_receive_bg.png")

interface Props {
  message: Message
  onOpen: () => void,
  onClose: () => void
}

const TransferReceivePopup: React.FC<Props> = ({ message, onOpen, onClose }) => {
  const theme = useTheme()
  const { walletStore } = useStores()
  const { openTransferDetailPopup } = useChatList()
  const { handleChatTransfer } = useGroupActions()
  const [loading, setLoading] = useState(false)
  const { attach = {}, sender = {} } = message
  const transInfo = attach?.trans_info

  const scale = useSharedValue(1)

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }))

  const onChatTransfer = useCallback(async () => {
    if (loading) return
    setLoading(true)
    handleChatTransfer({
        order_no: attach?.trans_info?.order_no,
    }, (data) => {
        walletStore.getWalletInfo()
        setLoading(false)
        onOpen()
        openTransferDetailPopup({ message, transferData: data?.transfer_data })
    }, (error) => {
        globalToast.show(error.message || "领取失败")
    })
  }, [loading, onOpen])

  const handlePress = useCallback(() => {
    scale.value = withSequence(
      withTiming(0.85, { duration: 80 }),
      withTiming(1.15, { duration: 120 }),
      withTiming(1, { duration: 80 }, () => {
        runOnJS(onChatTransfer)()
      })
    )
  }, [onChatTransfer])

  return (
    <PopupRoot width="100%">
      <PopupImageBackground src={transferReceiveBg} resizeMode="cover">
        <ColumnFlex width="100%" height="100%" alignItems="center">
          <CenterFlex
            width="60px"
            height="60px"
            padding="2px"
            mt="50px"
            borderRadius="30px"
            bg={theme.colors.blockBackground}
          >
            <Avatar avatar={sender?.avatar} size={56} name={sender?.name} />
          </CenterFlex>

          <Text mt="8px" fontSize={18} color={theme.colors.textLight} fontWeight="600">
            {sender?.name || ""}向您转账
          </Text>
          <Text mt="4px" fontSize={14} color={theme.colors.textLight}>请确认收款</Text>
          <Text mt="8px" fontSize={14} color={theme.colors.textLight}>
            "{transInfo?.note || "恭喜发财，大吉大利"}"
          </Text>

          <Text mt="15px" fontSize={40} color={theme.colors.warning} fontWeight="600">
            ¥{transInfo?.amount || "0"}
          </Text>
          <Text fontSize={12} color={theme.colors.textLight}>点击下方按钮确认收款</Text>

          <Animated.View style={[{ marginTop: 30 }, animatedStyle]}>
            <Button
              width="72px"
              height="72px"
              activeOpacity={1.0}
              disabled={loading}
              onClick={handlePress}
            >
              <Image
                width="100%"
                height="100%"
                src={require("~assets/images/chat/receive_btn.png")}
              />
            </Button>
          </Animated.View>
        </ColumnFlex>
      </PopupImageBackground>
      <PopupClose onClose={onClose} />
    </PopupRoot>
  )
}

export default TransferReceivePopup
